import type { TierRow } from "@ow/shared";

// The admin "what needs my attention" model — NOT a second dataset: it re-cuts the same live roles into
// the handful that need a decision or a nudge. Shared by the Needs attention page (grouped by kind) and
// the nav count. The client never sees any of it.

export type Kind = "stalled" | "awaiting" | "missing" | "draft";

// Each kind carries a SEMANTIC severity colour (never the brand accent) — surfaced on the group as a dot +
// colour-coded tally so urgency reads at a glance: stalled = red (blocked), awaiting = amber (waiting on
// someone), missing = blue (needs input), draft = neutral (internal, not yet live). Ordered most-urgent first.
export const KIND: Record<Kind, { label: string; color: string }> = {
  stalled: { label: "Stalled", color: "var(--color-destructive)" },
  awaiting: { label: "Awaiting client", color: "var(--color-warning)" },
  missing: { label: "Missing data", color: "var(--color-info)" },
  draft: { label: "Draft role", color: "var(--muted-foreground)" },
};
export const KIND_ORDER: Kind[] = ["stalled", "awaiting", "missing", "draft"];

export type Item = { kind: Kind; role: TierRow; person: string | null; ask: string; age: string };

/** Representative work items, anchored to real roles (so each links through) — the queue a search lead works
 *  down. Static for this design pass; devs derive the live queue from stage age, offer state, and data
 *  completeness. */
export function buildItems(roles: TierRow[]): Item[] {
  if (roles.length === 0) return [];
  const r = (i: number) => roles[i % roles.length] as TierRow;
  return [
    {
      kind: "stalled",
      role: r(0),
      person: "Marcus Feld",
      ask: "12 days in the final panel — chase the client for feedback",
      age: "12d",
    },
    {
      kind: "awaiting",
      role: r(1),
      person: "Jane Okafor",
      ask: "Offer out 6 days — awaiting the candidate's decision",
      age: "6d",
    },
    {
      kind: "draft",
      role: r(2),
      person: null,
      ask: "Pipeline drafted but not launched — the client can't see it yet",
      age: "3d",
    },
    {
      kind: "stalled",
      role: r(3),
      person: "Priya Nair",
      ask: "9 days in first round — schedule the next interview",
      age: "9d",
    },
    { kind: "missing", role: r(4), person: "Devon Cole", ask: "No CV or compensation on file yet", age: "—" },
    {
      kind: "awaiting",
      role: r(5),
      person: "Tom Bradley",
      ask: "Reference check pending the client's sign-off",
      age: "4d",
    },
  ];
}

/** The live open count for the nav badge — representative here (matches the seed queue); devs wire the
 *  real figure alongside the queue. */
export const OPEN_COUNT = 6;

/** Rank most-urgent first: by severity (KIND_ORDER), then by age within a kind. */
export function rankItems(items: Item[]): Item[] {
  const ageOf = (a: string) => (a.endsWith("d") ? Number.parseInt(a, 10) || 0 : 0);
  return [...items].sort((a, b) => {
    const bySeverity = KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
    return bySeverity !== 0 ? bySeverity : ageOf(b.age) - ageOf(a.age);
  });
}
